import { useState, useRef, useCallback, useEffect, useMemo } from "react";

const ANIMATION_STATUS_ICONS = {
  completed: { icon: "🎬", color: "#22c55e", label: "Animated" },
  processing: { icon: "⏳", color: "#eab308", label: "Processing" },
  pending: { icon: "⏸️", color: "#6b7280", label: "Pending" },
  failed: { icon: "❌", color: "#ef4444", label: "Failed" },
  skipped: { icon: "⏭️", color: "#6b7280", label: "Skipped" },
  static: { icon: "🖼️", color: "#3b82f6", label: "Static" },
};

const TRACK_HEIGHT = 56;
const RULER_HEIGHT = 28;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 4;

function formatTime(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  const ms = Math.floor((seconds % 1) * 10);
  return `${mins}:${secs.toString().padStart(2, "0")}.${ms}`;
}

function formatTimeShort(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

function TimeRuler({ duration, zoom, scrollLeft }) {
  const interval = useMemo(() => {
    if (zoom >= 3) return 1;
    if (zoom >= 1.5) return 2;
    if (zoom >= 0.8) return 5;
    return 10;
  }, [zoom]);

  const markers = useMemo(() => {
    const result = [];
    for (let t = 0; t <= duration; t += interval) {
      result.push(t);
    }
    return result;
  }, [duration, interval]);

  return (
    <div
      className="relative select-none"
      style={{ height: RULER_HEIGHT, background: "#f8fafc" }}
    >
      {markers.map((time) => {
        const left = (time / duration) * 100;
        return (
          <div
            key={time}
            className="absolute top-0 bottom-0"
            style={{ left: `${left}%` }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: 1,
                height: "100%",
                background: "#e2e8f0",
              }}
            />
            <span
              style={{
                position: "absolute",
                top: 2,
                left: 4,
                fontSize: 10,
                color: "#64748b",
                whiteSpace: "nowrap",
                userSelect: "none",
              }}
            >
              {formatTimeShort(time)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function TrackLabel({ label, icon, color }) {
  return (
    <div
      className="flex items-center gap-1.5 px-3 shrink-0"
      style={{
        width: 100,
        height: TRACK_HEIGHT,
        background: "#f8fafc",
        borderRight: "1px solid #e2e8f0",
        borderBottom: "1px solid #e2e8f0",
      }}
    >
      <span style={{ fontSize: 12 }}>{icon}</span>
      <span
        style={{ fontSize: 11, fontWeight: 500, color: color || "#64748b" }}
      >
        {label}
      </span>
    </div>
  );
}

export default function RemotionTimeline({
  duration,
  segments,
  onSegmentsChange,
  currentTime,
  onSeek,
  isPlaying,
  onRegenerateImage,
  onViewImageHistory,
  onRegenerateAnimation,
  onSkipAnimation,
  onGenerateAnimation,
  animateVisuals = false,
  audioUrl,
  sfxClips = [],
  onSfxClipSelect,
  onSfxClipUpdate,
  onSfxClipDelete,
  selectedSfxClipId = null,
}) {
  const timelineRef = useRef(null);
  const trackAreaRef = useRef(null);
  const [dragState, setDragState] = useState(null);
  const [hoveredSegment, setHoveredSegment] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [sfxDragState, setSfxDragState] = useState(null);

  const activeSegmentIndex = segments.findIndex(
    (seg) => currentTime >= seg.startTime && currentTime < seg.endTime
  );

  const timeToPercent = useCallback(
    (time) => (duration > 0 ? (time / duration) * 100 : 0),
    [duration]
  );

  const getMouseTime = useCallback(
    (e) => {
      if (!trackAreaRef.current) return 0;
      const rect = trackAreaRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
      return (x / rect.width) * duration;
    },
    [duration]
  );

  // Timeline click → seek
  const handleTimelineClick = (e) => {
    if (dragState || sfxDragState) return;
    if (e.target.closest(".segment-block") || e.target.closest(".sfx-clip"))
      return;
    const time = getMouseTime(e);
    onSeek(time);
  };

  // Segment edge drag
  const handleEdgeDragStart = (e, segmentIndex, edge) => {
    e.stopPropagation();
    e.preventDefault();
    setDragState({ type: "edge", segmentIndex, edge });
  };

  // Segment drag mouse move
  const handleMouseMove = useCallback(
    (e) => {
      if (!dragState) return;
      const newTime = getMouseTime(e);
      const newSegments = [...segments];

      if (dragState.type === "edge") {
        const { segmentIndex, edge } = dragState;
        const segment = { ...newSegments[segmentIndex] };

        if (edge === "start") {
          const minTime =
            segmentIndex > 0 ? newSegments[segmentIndex - 1].startTime : 0;
          const maxTime = segment.endTime - 0.5;
          segment.startTime = Math.max(minTime, Math.min(newTime, maxTime));
          if (segmentIndex > 0) {
            newSegments[segmentIndex - 1] = {
              ...newSegments[segmentIndex - 1],
              endTime: segment.startTime,
            };
          }
        } else {
          const minTime = segment.startTime + 0.5;
          const maxTime =
            segmentIndex < segments.length - 1
              ? newSegments[segmentIndex + 1].endTime
              : duration;
          segment.endTime = Math.max(minTime, Math.min(newTime, maxTime));
          if (segmentIndex < segments.length - 1) {
            newSegments[segmentIndex + 1] = {
              ...newSegments[segmentIndex + 1],
              startTime: segment.endTime,
            };
          }
        }

        newSegments[segmentIndex] = segment;
        onSegmentsChange(newSegments);
      }
    },
    [dragState, segments, getMouseTime, duration, onSegmentsChange]
  );

  const handleMouseUp = useCallback(() => {
    setDragState(null);
  }, []);

  useEffect(() => {
    if (dragState) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [dragState, handleMouseMove, handleMouseUp]);

  // SFX clip drag
  const handleSfxMouseDown = (e, clip, edge = null) => {
    e.stopPropagation();
    onSfxClipSelect?.(clip);
    setSfxDragState({
      clipId: clip.id,
      edge,
      startX: e.clientX,
      origStart: clip.start_time,
      origEnd: clip.end_time,
    });
  };

  const handleSfxMouseMove = useCallback(
    (e) => {
      if (!sfxDragState || !trackAreaRef.current) return;
      const clip = sfxClips.find((c) => c.id === sfxDragState.clipId);
      if (!clip) return;

      const rect = trackAreaRef.current.getBoundingClientRect();
      const deltaX = e.clientX - sfxDragState.startX;
      const deltaTime = (deltaX / rect.width) * duration;

      if (sfxDragState.edge === "start") {
        const newStart = Math.max(
          0,
          Math.min(clip.end_time - 0.5, sfxDragState.origStart + deltaTime)
        );
        onSfxClipUpdate?.(clip.id, { start_time: newStart });
      } else if (sfxDragState.edge === "end") {
        const newEnd = Math.max(
          clip.start_time + 0.5,
          Math.min(duration, sfxDragState.origEnd + deltaTime)
        );
        onSfxClipUpdate?.(clip.id, { end_time: newEnd });
      } else {
        const clipDur = sfxDragState.origEnd - sfxDragState.origStart;
        let newStart = sfxDragState.origStart + deltaTime;
        newStart = Math.max(0, Math.min(duration - clipDur, newStart));
        onSfxClipUpdate?.(clip.id, {
          start_time: newStart,
          end_time: newStart + clipDur,
        });
      }
    },
    [sfxDragState, sfxClips, duration, onSfxClipUpdate]
  );

  const handleSfxMouseUp = useCallback(() => {
    setSfxDragState(null);
  }, []);

  useEffect(() => {
    if (sfxDragState) {
      window.addEventListener("mousemove", handleSfxMouseMove);
      window.addEventListener("mouseup", handleSfxMouseUp);
      return () => {
        window.removeEventListener("mousemove", handleSfxMouseMove);
        window.removeEventListener("mouseup", handleSfxMouseUp);
      };
    }
  }, [sfxDragState, handleSfxMouseMove, handleSfxMouseUp]);

  // Split segment
  const handleSplitSegment = (segmentIndex) => {
    const segment = segments[segmentIndex];
    if (currentTime <= segment.startTime || currentTime >= segment.endTime)
      return;
    const newSegments = [...segments];
    const newSegment = {
      ...segment,
      id: `${segment.id}-split-${Date.now()}`,
      startTime: currentTime,
    };
    newSegments[segmentIndex] = { ...segment, endTime: currentTime };
    newSegments.splice(segmentIndex + 1, 0, newSegment);
    onSegmentsChange(newSegments);
    setContextMenu(null);
  };

  const handleDeleteSegment = (segmentIndex) => {
    if (segments.length <= 1) return;
    const newSegments = [...segments];
    const deleted = newSegments[segmentIndex];
    if (segmentIndex > 0) {
      newSegments[segmentIndex - 1] = {
        ...newSegments[segmentIndex - 1],
        endTime: deleted.endTime,
      };
    } else if (segmentIndex < segments.length - 1) {
      newSegments[segmentIndex + 1] = {
        ...newSegments[segmentIndex + 1],
        startTime: deleted.startTime,
      };
    }
    newSegments.splice(segmentIndex, 1);
    onSegmentsChange(newSegments);
    setContextMenu(null);
  };

  const handleContextMenu = (e, segmentIndex) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, segmentIndex });
  };

  useEffect(() => {
    const close = () => setContextMenu(null);
    if (contextMenu) {
      window.addEventListener("click", close);
      return () => window.removeEventListener("click", close);
    }
  }, [contextMenu]);

  const playheadPercent = timeToPercent(currentTime);

  return (
    <div
      ref={timelineRef}
      style={{
        background: "#ffffff",
        borderRadius: 12,
        overflow: "hidden",
        border: "1px solid #e2e8f0",
        userSelect: "none",
      }}
    >
      {/* Toolbar */}
      <div
        className="flex items-center justify-between px-3"
        style={{
          height: 36,
          background: "#f8fafc",
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <div className="flex items-center gap-3">
          <span
            style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}
          >
            Timeline
          </span>
          <span
            style={{
              fontSize: 11,
              color: "#64748b",
              fontFamily: "monospace",
            }}
          >
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setZoom((z) => Math.max(MIN_ZOOM, z - 0.25))}
            style={{
              width: 24,
              height: 24,
              borderRadius: 4,
              background: "#f1f5f9",
              color: "#64748b",
              border: "1px solid #e2e8f0",
              cursor: "pointer",
              fontSize: 14,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title="Zoom out"
          >
            −
          </button>
          <span
            style={{
              fontSize: 10,
              color: "#64748b",
              minWidth: 32,
              textAlign: "center",
            }}
          >
            {Math.round(zoom * 100)}%
          </span>
          <button
            onClick={() => setZoom((z) => Math.min(MAX_ZOOM, z + 0.25))}
            style={{
              width: 24,
              height: 24,
              borderRadius: 4,
              background: "#f1f5f9",
              color: "#64748b",
              border: "1px solid #e2e8f0",
              cursor: "pointer",
              fontSize: 14,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title="Zoom in"
          >
            +
          </button>
        </div>
      </div>

      {/* Tracks area */}
      <div style={{ overflowX: "auto", overflowY: "hidden" }}>
        <div style={{ minWidth: `${zoom * 100}%` }}>
          {/* Ruler row */}
          <div className="flex">
            <div
              style={{
                width: 100,
                height: RULER_HEIGHT,
                background: "#f8fafc",
                borderRight: "1px solid #e2e8f0",
                borderBottom: "1px solid #e2e8f0",
                flexShrink: 0,
              }}
            />
            <div
              ref={trackAreaRef}
              className="flex-1 relative"
              style={{ borderBottom: "1px solid #e2e8f0" }}
              onClick={handleTimelineClick}
            >
              <TimeRuler duration={duration} zoom={zoom} />
            </div>
          </div>

          {/* Video Track */}
          <div className="flex">
            <TrackLabel label="Video" icon="🎬" color="#7c3aed" />
            <div
              className="flex-1 relative"
              style={{
                height: TRACK_HEIGHT,
                background: "#ffffff",
                borderBottom: "1px solid #e2e8f0",
              }}
              onClick={handleTimelineClick}
            >
              {/* Grid lines */}
              {[25, 50, 75].map((pct) => (
                <div
                  key={pct}
                  className="absolute top-0 bottom-0"
                  style={{
                    left: `${pct}%`,
                    width: 1,
                    background: "#f1f5f9",
                  }}
                />
              ))}

              {/* Segments */}
              {segments.map((segment, index) => {
                const left = timeToPercent(segment.startTime);
                const width = timeToPercent(
                  segment.endTime - segment.startTime
                );
                const isActive = index === activeSegmentIndex;
                const isHovered = index === hoveredSegment;
                const statusInfo =
                  animateVisuals && segment.animation_status
                    ? ANIMATION_STATUS_ICONS[segment.animation_status]
                    : null;

                return (
                  <div
                    key={segment.id}
                    className="segment-block absolute"
                    style={{
                      left: `${left}%`,
                      width: `${width}%`,
                      top: 3,
                      bottom: 3,
                      borderRadius: 6,
                      overflow: "hidden",
                      border: isActive
                        ? "2px solid #7c3aed"
                        : isHovered
                          ? "2px solid #8b5cf6"
                          : "1px solid #cbd5e1",
                      cursor: dragState ? "grabbing" : "grab",
                      transition: "border-color 0.15s",
                    }}
                    onMouseEnter={() => setHoveredSegment(index)}
                    onMouseLeave={() => setHoveredSegment(null)}
                    onContextMenu={(e) => handleContextMenu(e, index)}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSeek(segment.startTime);
                    }}
                  >
                    {/* Thumbnail background */}
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        backgroundImage: `url(${segment.image_url})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        opacity: 0.6,
                      }}
                    />
                    {/* Gradient overlay */}
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        background:
                          "linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(0,0,0,0.55) 100%)",
                      }}
                    />
                    {/* Label */}
                    <div
                      style={{
                        position: "absolute",
                        bottom: 2,
                        left: 4,
                        right: 4,
                        display: "flex",
                        alignItems: "center",
                        gap: 3,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          color: "#ffffff",
                          textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                        }}
                      >
                        {index + 1}
                      </span>
                      <span
                        style={{
                          fontSize: 9,
                          color: "#e2e8f0",
                          fontFamily: "monospace",
                          textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                        }}
                      >
                        {formatTimeShort(
                          segment.endTime - segment.startTime
                        )}
                      </span>
                      {statusInfo && (
                        <span
                          style={{ fontSize: 10 }}
                          title={statusInfo.label}
                        >
                          {statusInfo.icon}
                        </span>
                      )}
                    </div>

                    {/* Left edge handle */}
                    <div
                      className="edge-handle"
                      style={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 6,
                        cursor: "ew-resize",
                        background:
                          "linear-gradient(90deg, rgba(124,58,237,0.5) 0%, transparent 100%)",
                      }}
                      onMouseDown={(e) =>
                        handleEdgeDragStart(e, index, "start")
                      }
                    />
                    {/* Right edge handle */}
                    <div
                      className="edge-handle"
                      style={{
                        position: "absolute",
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: 6,
                        cursor: "ew-resize",
                        background:
                          "linear-gradient(270deg, rgba(124,58,237,0.5) 0%, transparent 100%)",
                      }}
                      onMouseDown={(e) =>
                        handleEdgeDragStart(e, index, "end")
                      }
                    />
                  </div>
                );
              })}

              {/* Playhead */}
              <div
                className="absolute top-0 bottom-0 pointer-events-none"
                style={{
                  left: `${playheadPercent}%`,
                  width: 2,
                  background: "#ef4444",
                  zIndex: 20,
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: -2,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: "#ef4444",
                  }}
                />
              </div>
            </div>
          </div>

          {/* Audio Track */}
          <div className="flex">
            <TrackLabel label="Audio" icon="🎵" color="#2563eb" />
            <div
              className="flex-1 relative"
              style={{
                height: TRACK_HEIGHT,
                background: "#fafafa",
                borderBottom: "1px solid #e2e8f0",
              }}
              onClick={handleTimelineClick}
            >
              {/* Audio bar */}
              {audioUrl && (
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    top: 6,
                    bottom: 6,
                    borderRadius: 4,
                    background:
                      "linear-gradient(90deg, #dbeafe 0%, #bfdbfe 25%, #93c5fd 50%, #bfdbfe 75%, #dbeafe 100%)",
                    opacity: 0.8,
                  }}
                >
                  {/* Faux waveform bars */}
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-evenly",
                      gap: 1,
                      padding: "0 4px",
                      overflow: "hidden",
                    }}
                  >
                    {Array.from({ length: 80 }).map((_, i) => {
                      const h =
                        20 +
                        Math.sin(i * 0.5) * 30 +
                        Math.cos(i * 0.3) * 20;
                      return (
                        <div
                          key={i}
                          style={{
                            width: 2,
                            height: `${Math.min(100, Math.max(10, h))}%`,
                            background: "#3b82f6",
                            borderRadius: 1,
                            opacity: 0.5,
                            flexShrink: 0,
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Playhead */}
              <div
                className="absolute top-0 bottom-0 pointer-events-none"
                style={{
                  left: `${playheadPercent}%`,
                  width: 2,
                  background: "#ef4444",
                  zIndex: 20,
                }}
              />
            </div>
          </div>

          {/* SFX Track */}
          <div className="flex">
            <TrackLabel label="SFX" icon="🔊" color="#7c3aed" />
            <div
              className="flex-1 relative"
              style={{
                height: TRACK_HEIGHT,
                background: "#fafafa",
                borderBottom: "1px solid #e2e8f0",
              }}
              onClick={(e) => {
                if (!e.target.closest(".sfx-clip")) {
                  onSfxClipSelect?.(null);
                  handleTimelineClick(e);
                }
              }}
            >
              {/* SFX clips */}
              {sfxClips.map((clip) => {
                const left = timeToPercent(clip.start_time);
                const width = timeToPercent(clip.end_time - clip.start_time);
                const isSelected = selectedSfxClipId === clip.id;

                return (
                  <div
                    key={clip.id}
                    className="sfx-clip absolute"
                    style={{
                      left: `${left}%`,
                      width: `${Math.max(width, 1.5)}%`,
                      top: 6,
                      bottom: 6,
                      borderRadius: 4,
                      background: isSelected ? "#ede9fe" : "#f5f3ff",
                      border: isSelected
                        ? "2px solid #7c3aed"
                        : "1px solid #c4b5fd",
                      cursor: sfxDragState ? "grabbing" : "grab",
                      overflow: "hidden",
                      transition: "background 0.15s, border-color 0.15s",
                    }}
                    onMouseDown={(e) => handleSfxMouseDown(e, clip)}
                  >
                    {/* Left resize handle */}
                    <div
                      style={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 5,
                        cursor: "ew-resize",
                      }}
                      onMouseDown={(e) =>
                        handleSfxMouseDown(e, clip, "start")
                      }
                    />

                    <div
                      style={{
                        padding: "0 6px",
                        display: "flex",
                        alignItems: "center",
                        height: "100%",
                        overflow: "hidden",
                      }}
                    >
                      <span
                        style={{
                          fontSize: 10,
                          color: "#5b21b6",
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {clip.asset?.name || "SFX"}
                      </span>
                    </div>

                    {/* Right resize handle */}
                    <div
                      style={{
                        position: "absolute",
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: 5,
                        cursor: "ew-resize",
                      }}
                      onMouseDown={(e) =>
                        handleSfxMouseDown(e, clip, "end")
                      }
                    />
                  </div>
                );
              })}

              {sfxClips.length === 0 && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <span style={{ fontSize: 11, color: "#94a3b8" }}>
                    No sound effects
                  </span>
                </div>
              )}

              {/* Playhead */}
              <div
                className="absolute top-0 bottom-0 pointer-events-none"
                style={{
                  left: `${playheadPercent}%`,
                  width: 2,
                  background: "#ef4444",
                  zIndex: 20,
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Segment thumbnails strip */}
      <div
        style={{
          padding: "8px 12px",
          background: "#f8fafc",
          borderTop: "1px solid #e2e8f0",
        }}
      >
        <div className="flex gap-2 overflow-x-auto pb-1">
          {segments.map((segment, index) => (
            <div
              key={segment.id}
              onClick={() => onSeek(segment.startTime)}
              style={{
                flexShrink: 0,
                width: 72,
                borderRadius: 6,
                overflow: "hidden",
                border:
                  index === activeSegmentIndex
                    ? "2px solid #7c3aed"
                    : "1px solid #e2e8f0",
                cursor: "pointer",
                transition: "border-color 0.15s",
              }}
            >
              <div
                style={{
                  height: 40,
                  background: "#f1f5f9",
                  position: "relative",
                }}
              >
                {segment.animated_video_url &&
                segment.animation_status === "completed" ? (
                  <video
                    src={segment.animated_video_url}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                    muted
                    loop
                    onMouseEnter={(e) => e.target.play()}
                    onMouseLeave={(e) => {
                      e.target.pause();
                      e.target.currentTime = 0;
                    }}
                  />
                ) : (
                  <img
                    src={segment.image_url}
                    alt={`Segment ${index + 1}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                )}
                {animateVisuals && segment.animation_status && (
                  <div
                    style={{
                      position: "absolute",
                      top: 2,
                      right: 2,
                      fontSize: 10,
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      background: "rgba(0,0,0,0.5)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {ANIMATION_STATUS_ICONS[segment.animation_status]
                      ?.icon || "🖼️"}
                  </div>
                )}
              </div>
              <div
                style={{
                  padding: "2px 4px",
                  background: "#f8fafc",
                  textAlign: "center",
                }}
              >
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: "#334155",
                  }}
                >
                  {index + 1}
                </span>
                <span
                  style={{
                    fontSize: 9,
                    color: "#94a3b8",
                    marginLeft: 4,
                    fontFamily: "monospace",
                  }}
                >
                  {formatTimeShort(segment.endTime - segment.startTime)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Keyboard hints */}
      <div
        style={{
          padding: "6px 12px",
          background: "#f8fafc",
          borderTop: "1px solid #e2e8f0",
          display: "flex",
          gap: 16,
        }}
      >
        <span style={{ fontSize: 10, color: "#94a3b8" }}>
          <strong style={{ color: "#64748b" }}>Drag edges</strong> to trim
        </span>
        <span style={{ fontSize: 10, color: "#94a3b8" }}>
          <strong style={{ color: "#64748b" }}>Right-click</strong> for
          options
        </span>
        <span style={{ fontSize: 10, color: "#94a3b8" }}>
          <strong style={{ color: "#64748b" }}>Click</strong> to seek
        </span>
        <span style={{ fontSize: 10, color: "#94a3b8" }}>
          <strong style={{ color: "#64748b" }}>Space</strong> play/pause
        </span>
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="fixed z-50"
          style={{
            left: contextMenu.x,
            top: contextMenu.y,
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            padding: "4px 0",
            minWidth: 180,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
          }}
        >
          {onRegenerateImage && (
            <button
              onClick={() => {
                onRegenerateImage(segments[contextMenu.segmentIndex]);
                setContextMenu(null);
              }}
              style={menuItemStyle}
              onMouseEnter={(e) =>
                (e.target.style.background = "#f1f5f9")
              }
              onMouseLeave={(e) =>
                (e.target.style.background = "transparent")
              }
            >
              <span>🔄</span> Regenerate Image
            </button>
          )}
          {onViewImageHistory && (
            <button
              onClick={() => {
                onViewImageHistory(segments[contextMenu.segmentIndex]);
                setContextMenu(null);
              }}
              style={menuItemStyle}
              onMouseEnter={(e) =>
                (e.target.style.background = "#f1f5f9")
              }
              onMouseLeave={(e) =>
                (e.target.style.background = "transparent")
              }
            >
              <span>📜</span> View Image History
            </button>
          )}
          {(onRegenerateImage || onViewImageHistory) && (
            <div
              style={{
                height: 1,
                background: "#e2e8f0",
                margin: "4px 0",
              }}
            />
          )}
          {animateVisuals && (
            <>
              {onGenerateAnimation &&
                segments[contextMenu.segmentIndex]?.animation_status !==
                  "completed" &&
                segments[contextMenu.segmentIndex]?.animation_status !==
                  "processing" && (
                  <button
                    onClick={() => {
                      onGenerateAnimation(
                        segments[contextMenu.segmentIndex]
                      );
                      setContextMenu(null);
                    }}
                    style={menuItemStyle}
                    onMouseEnter={(e) =>
                      (e.target.style.background = "#f1f5f9")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.background = "transparent")
                    }
                  >
                    <span>✨</span> Generate Animation
                  </button>
                )}
              {onRegenerateAnimation &&
                (segments[contextMenu.segmentIndex]?.animation_status ===
                  "completed" ||
                  segments[contextMenu.segmentIndex]?.animation_status ===
                    "failed") && (
                  <button
                    onClick={() => {
                      onRegenerateAnimation(
                        segments[contextMenu.segmentIndex]
                      );
                      setContextMenu(null);
                    }}
                    style={menuItemStyle}
                    onMouseEnter={(e) =>
                      (e.target.style.background = "#f1f5f9")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.background = "transparent")
                    }
                  >
                    <span>🔄</span> Regenerate Animation
                  </button>
                )}
              {onSkipAnimation &&
                (segments[contextMenu.segmentIndex]?.animation_status ===
                  "pending" ||
                  segments[contextMenu.segmentIndex]?.animation_status ===
                    "failed") && (
                  <button
                    onClick={() => {
                      onSkipAnimation(
                        segments[contextMenu.segmentIndex]
                      );
                      setContextMenu(null);
                    }}
                    style={menuItemStyle}
                    onMouseEnter={(e) =>
                      (e.target.style.background = "#f1f5f9")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.background = "transparent")
                    }
                  >
                    <span>⏭️</span> Skip Animation
                  </button>
                )}
              <div
                style={{
                  height: 1,
                  background: "#e2e8f0",
                  margin: "4px 0",
                }}
              />
            </>
          )}
          <button
            onClick={() => handleSplitSegment(contextMenu.segmentIndex)}
            style={menuItemStyle}
            onMouseEnter={(e) =>
              (e.target.style.background = "#f1f5f9")
            }
            onMouseLeave={(e) =>
              (e.target.style.background = "transparent")
            }
          >
            <span>✂️</span> Split at playhead
          </button>
          {segments.length > 1 && (
            <button
              onClick={() =>
                handleDeleteSegment(contextMenu.segmentIndex)
              }
              style={{ ...menuItemStyle, color: "#ef4444" }}
              onMouseEnter={(e) =>
                (e.target.style.background = "#f1f5f9")
              }
              onMouseLeave={(e) =>
                (e.target.style.background = "transparent")
              }
            >
              <span>🗑️</span> Delete segment
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const menuItemStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  width: "100%",
  padding: "6px 12px",
  fontSize: 12,
  color: "#334155",
  background: "transparent",
  border: "none",
  cursor: "pointer",
  textAlign: "left",
};
